import React, { useState, useEffect, useRef } from 'react';
import '../view-archer-styles.css';

// material-ui: theme, colors, icons
import { Avatar, Button, Chip, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, DialogContentText, FormControl, FormControlLabel, MenuItem, InputLabel, Select, TextField, Grid, IconButton, Paper, Slide, Stack, Typography } from '@mui/material/';
import { TransitionProps } from '@mui/material/transitions';
import { blue } from '@mui/material/colors';
import { Close, EditNoteOutlined } from '@mui/icons-material/';

//components import
import AlertInfoDialog from '../../../components/elements/AlertInfoDialog';
import AlertInfo from '../../../dataObjects/AlertInfo';
import ArcherIssueType from '../dataObjects/ArcherIssueType';

//const data
import GatewayIssueCategories from '../data/GatewayIssueCategories';
import IssueTypeSortCategories from '../data/IssueTypeSortCategories';
import ExpectedResponseLevel from '../data/ExpectedResponseLevel';

//third parties:
import { Editor } from '@tinymce/tinymce-react';
//import { Editor as TinyMCEEditor } from 'tinymce'

interface IssueTemplateEditorProps {
    isIssueTemplateEditorOpen: boolean,
    closeIssueTemplateEditor: () => void
}
interface IssueType {
    issueTypeCode: number;
    description: string;
    display: string;
}

const Transition = React.forwardRef(function Transition(
    props: TransitionProps & {
        children: React.ReactElement<any, any>;
    },
    ref: React.Ref<unknown>,
) {
    return <Slide direction="right" ref={ref} {...props} />;
});

const ArcherIssueTemplateEditor = (props: IssueTemplateEditorProps) => {

    useEffect(() => {
        getIssueTypeListing(currentIssueTypeCode);
    }, [])

    //initial state setup
    const [issueTypes, setIssueTypes] = useState<IssueType[]>();
    const [fullIssueTypeListing, setFullIssueTypeListing] = useState<IssueType[]>();
    const [currentIssueTypeCode, setCurrentIssueTypeCode] = useState(4);
    const [newIssueTypeCategoryCode, setNewIssueTypeCategory] = useState(100); // receipts related
    const [isNewIssue, setIsNewIssue] = useState(false);
    const [currentIssue, setCurrentIssue] = useState<ArcherIssueType>({
        issueTypeCode: 0,
        header: "",
        hint: "",
        sortCategoryId: 0,
        gatewayIssueCategoryId: 0,
        expectedResponseLevelId: 0,
        body: "",
        instructions: "",
        isResponseRequired: true
    });

    const closeIssueTemplateEditor = () => {
        props.closeIssueTemplateEditor();
    }

    /*TEXT EDITOR SET UP*/
    const editorAPIKey = process.env.REACT_APP_EDITOR_API_KEY;
    const editorBodyRef = useRef<any>(null);
    const editorInstructionsRef = useRef<any>(null);

    const addNewIssue = () => {
        setIsNewIssue(true);
        setNewIssueTypeCategory(100);
        getNextIssueTypeCode(100);
    }

    const getIssueTypeListing = (currentIssueTypeCode: number) => {

        let url = `/Archer3/GetIssueTypeListings`;
        fetch(url)
            .then(result => result.json())
            .then(result => {
                if (result.isError) {
                    let info = {
                        type: 'error',
                        title: 'Error: GetIssueTypeListings',
                        errorMessage: result.errorMessage,
                        secondaryErrorMessage: result.secondaryErrorMessage
                    };
                    setAlertInfo(info);
                    setAlertInfoDialog(true);
                }
                else {
                    setIssueTypes(result.result);
                    getIssueType(currentIssueTypeCode);
                    setFullIssueTypeListing(result.result);
                }
            })
    }

    const getIssueSearchResults = (event: any) => {
        let searchInput = event.target.value;
        setTimeout(() => {
            if (searchInput.length > 0) {
                let filteredIssues = fullIssueTypeListing?.filter(issue => (issue.description.toLowerCase().includes(searchInput.toLowerCase()) || issue.issueTypeCode === parseInt(searchInput)));
                setIssueTypes(filteredIssues);
                if (filteredIssues && filteredIssues.length>0) {
                    getIssueType(filteredIssues[0].issueTypeCode);
                }
                else {
                    setCurrentIssue({
                        issueTypeCode: 0,
                        header: "",
                        hint: "",
                        sortCategoryId: 0,
                        gatewayIssueCategoryId: 0,
                        expectedResponseLevelId: 0,
                        body: "",
                        instructions: "",
                        isResponseRequired: true
                    });
                }
            }
            else {
                getIssueTypeListing(currentIssueTypeCode);
            }
        }, 1500)
    }

    const getIssueType = (issueTypeCode: number) => {
        let url = `/archer3/GetIssueType?issueTypeCode=${issueTypeCode}`;
            fetch(url)
            .then(result => result.json())
            .then(result => {
                if (result.isError) {
                    let info = {
                        type: 'error',
                        title: 'Error: GetIssueType',
                        errorMessage: result.errorMessage,
                        secondaryErrorMessage: result.secondaryErrorMessage
                    };
                    setAlertInfo(info);
                    setAlertInfoDialog(true);
                }
                else {
                    let issue = result.result;
                    setCurrentIssue(issue);
                    if (editorBodyRef.current) {
                        editorBodyRef.current.setContent(issue.body);
                    };

                    if (editorInstructionsRef.current) {
                        editorInstructionsRef.current.setContent(issue.instructions);
                    };
                }

            })
    }

    const selectIssueType = (issue: IssueType) => {
        setCurrentIssueTypeCode(issue.issueTypeCode);
        getIssueType(issue.issueTypeCode);
        setIsNewIssue(false);
    }

    const updateNewIssueTypeCategory = (event: any) => {
        let newCategory = event.target.value;
        setNewIssueTypeCategory(newCategory);
        getNextIssueTypeCode(newCategory);
    }

    const updateIssueType = (event: any) => {
        let name = event.target.name;
        let value = event.target.value;
        setCurrentIssue(prevState => ({ ...prevState, [name]: value }));
    }

    const getNextIssueTypeCode = (issueTypeRange: number) => {
        let url = `/archer3/getNextIssueTypeCode?issueTypeRange=${issueTypeRange}`;
        fetch(url)
            .then(result => result.json())
            .then(result => {
                if (result.isError) {
                    let info = {
                        type: 'error',
                        title: 'Error: GetNextIssueTypeCode',
                        errorMessage: result.errorMessage,
                        secondaryErrorMessage: result.secondaryErrorMessage
                    };
                    setAlertInfo(info);
                    setAlertInfoDialog(true);
                }
                else {
                    let newCode = result.result;
                    let issue = {
                        issueTypeCode: newCode,
                        header: "",
                        hint: "",
                        sortCategoryId: 0,
                        gatewayIssueCategoryId: 0,
                        expectedResponseLevelId: 0,
                        body: "",
                        instructions: "",
                        isResponseRequired: true
                    }
                    if (editorBodyRef.current) {
                        editorBodyRef.current.setContent('');
                    };

                    if (editorInstructionsRef.current) {
                        editorInstructionsRef.current.setContent('');
                    };
                    setCurrentIssue(issue);
                }
            })
    }

    const saveIssueType = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        const formData = new FormData(event.currentTarget);
        const formJson = Object.fromEntries((formData as any).entries());
        const isResponseRequired = formJson.isResponseRequired === 'on';

        let issueBody = '';
        if (editorBodyRef.current) {
            issueBody = editorBodyRef.current.getContent();
        };
        let issueInstructions = '';
        if (editorInstructionsRef.current) {
            issueInstructions = editorInstructionsRef.current.getContent();
        };

        fetch('/Archer3/SaveIssueType', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(
                {
                    issueTypeCode: formJson.issueTypeCode,
                    header: formJson.header,
                    hint: formJson.hint,
                    gatewayIssueCategoryId: formJson.gatewayIssueCategoryId,
                    sortCategoryId: formJson.sortCategoryId,
                    expectedResponseLevelId: formJson.expectedResponseLevelId,
                    isResponseRequired: isResponseRequired,
                    body: issueBody,
                    instructions: issueInstructions
                })
            }).then(result => result.json())
            .then(result => {
                if (result.isError) {
                    let info = {
                        type: 'error',
                        title: 'Error: SaveLetterBody',
                        errorMessage: result.errorMessage,
                        secondaryErrorMessage: result.secondaryErrorMessage
                    };
                    setAlertInfo(info);
                    setAlertInfoDialog(true);
                }
                else {
                    let successMessage = 'The issue is saved successfully';
                    let info = {
                        type: 'success',
                        title: 'Issue Saved',
                        errorMessage: successMessage,
                        secondaryErrorMessage: ''
                    };
                    setAlertInfo(info);
                    setAlertInfoDialog(true);
                    getIssueTypeListing(result.result.issueTypeCode);
                }
            });  
    }

    /*Error Handling*/
    const [isAlertInfoDialogOpen, setAlertInfoDialog] = useState(false);
    const [alertInfo, setAlertInfo] = useState<AlertInfo>();
    const closeAlertInfoDialog = () => {
        setAlertInfoDialog(false);
    }

    return (
        <>
            <Dialog
                open={props.isIssueTemplateEditorOpen}
                scroll={'paper'}
                fullWidth={true}
                fullScreen
                TransitionComponent={Transition}
                keepMounted
                onClose={closeIssueTemplateEditor}
                sx={{
                    "& .MuiDialog-container": {
                        justifyContent: "flex-end",
                        alignItems: "flex-end"
                    }
                }}
                PaperProps={{
                    component: 'form',
                    onSubmit: (event: React.FormEvent<HTMLFormElement>) => saveIssueType(event),
                    sx: {
                        m: 0,
                        top: 0,
                        right: 0,
                        bottom: 0,
                        minHeight: '100vh'
                    }
                }}
            >
                <DialogTitle sx={{ paddingTop: 1, paddingBottom:1}}>
                    <Stack direction="row" spacing={2}>
                        <Avatar sx={{ bgcolor: blue[500] }}><EditNoteOutlined /></Avatar>
                        <Typography sx={{ display: "flex", alignItems: "center", color: blue[800] }}>
                            Issue Type Editor
                        </Typography>
                    </Stack>
                </DialogTitle>
                <IconButton
                    aria-label="close"
                    onClick={closeIssueTemplateEditor}
                    sx={{
                        position: 'absolute',
                        right: 8,
                        top: 8,
                        color: (theme) => theme.palette.grey[500],
                    }}
                >
                    <Close />
                </IconButton>
                <DialogContent dividers>
                    <Grid container sx={{ marginTop: 1 }}>
                        <Grid item xs={2.5}>
                            <Paper sx={{ height: 800, p: 1, overflow: 'auto' }}>
                                <Stack spacing={1} direction="row" useFlexGap flexWrap="wrap">
                                    <TextField
                                        size="small"
                                        placeholder="Search Issue..."
                                        onChange={getIssueSearchResults}
                                    />
                                    <Button variant="outlined" size="small" onClick={addNewIssue}>Add New Issue</Button>
                                    {
                                        issueTypes &&
                                        issueTypes.map((issueType) =>
                                            <Chip key={issueType.issueTypeCode} label={`${issueType.issueTypeCode} ${issueType.description}`} color="primary" variant="outlined" className="is-clickable" onClick={() => selectIssueType(issueType)} />
                                        )
                                    }
                                </Stack>
                            </Paper>
                        </Grid>
                        <Grid item xs={0.5}></Grid>
                        <Grid item xs={8.5}>
                            <Paper sx={{p:1}} >
                                <Grid item xs={10}>
                                    {
                                        isNewIssue &&
                                        <FormControl fullWidth size="small">
                                            <InputLabel>New Issue Type Category</InputLabel>
                                            <Select
                                                label="New Issue Type Category"
                                                value={newIssueTypeCategoryCode}
                                                onChange={updateNewIssueTypeCategory}
                                            >
                                                <MenuItem value={100}>Receipt Related</MenuItem>
                                                <MenuItem value={300}>Expenditure Related</MenuItem>
                                                <MenuItem value={0}>Report Or Admin</MenuItem>
                                            </Select>
                                        </FormControl>
                                    }
                                    <TextField
                                        margin="normal"
                                        name="issueTypeCode"
                                        label="Issue Type Code (auto assigned)"
                                        variant="filled"
                                        fullWidth
                                        size="small"
                                        inputProps={
                                            { readOnly: true, }
                                        }
                                        value={currentIssue.issueTypeCode}
                                        onChange={updateIssueType}
                                    />
                                    <TextField
                                        margin="normal"
                                        name="header"
                                        label="Issue Header"
                                        fullWidth
                                        size="small"
                                        value={currentIssue.header}
                                        onChange={updateIssueType}
                                        required
                                    />
                                    <FormControl fullWidth required size="small" sx={{marginTop:2}}>
                                        <InputLabel>Gateway Category</InputLabel>
                                        <Select
                                            label="Gateway Group *"
                                            name="gatewayIssueCategoryId"
                                            value={currentIssue.gatewayIssueCategoryId}
                                            onChange={updateIssueType}
                                        >
                                            {
                                                GatewayIssueCategories.map(category =>
                                                    <MenuItem key={category.value} value={category.value}>{category.description}</MenuItem>
                                                )
                                            }
                                        </Select>
                                    </FormControl>
                                    <FormControl fullWidth required size="small" sx={{ marginTop: 2 }}>
                                        <InputLabel>Issue Sort Group</InputLabel>
                                        <Select
                                            label="Issue Sort Group *"
                                            name="sortCategoryId"
                                            value={currentIssue.sortCategoryId}
                                            onChange={updateIssueType}
                                        >
                                            {
                                                IssueTypeSortCategories.map(category =>
                                                    <MenuItem key={category.value} value={category.value}>{category.description}</MenuItem>
                                                )
                                            }
                                        </Select>
                                    </FormControl>
                                    <FormControl fullWidth required size="small" sx={{ marginTop: 2 }}>
                                        <InputLabel>Expected Response Level</InputLabel>
                                        <Select
                                            label="Expected Response Level *"
                                            name="expectedResponseLevelId"
                                            value={currentIssue.expectedResponseLevelId}
                                            onChange={updateIssueType}
                                        >
                                            {
                                                ExpectedResponseLevel.map(level =>
                                                    <MenuItem key={level.value} value={level.value}>{level.description}</MenuItem>
                                                )
                                            }
                                        </Select>
                                    </FormControl>
                                    <TextField
                                        margin="normal"
                                        name="hint"
                                        label="Issue Hint"
                                        fullWidth
                                        size="small"
                                        value={currentIssue.hint}
                                        onChange={updateIssueType}
                                    />
                                    <FormControlLabel
                                        label="Is Response Required"
                                        control={<Checkbox size="small" name="isResponseRequired" checked={currentIssue.isResponseRequired} onChange={updateIssueType} />}
                                    />
                                    <DialogContentText>
                                        <Typography variant="subtitle1" sx={{ color: blue[800] }}>Issue Body</Typography>
                                    </DialogContentText>
                                    <Editor
                                        apiKey={editorAPIKey}
                                        onInit={(evt: any, editor: any) => editorBodyRef.current = editor}
                                        initialValue={"Type here for issue body..."}
                                        init={{
                                            height: 400,
                                            menubar: true,
                                            branding: false,
                                            plugins: [
                                                'advlist', 'autolink', 'lists', 'link', 'image', 'charmap', 'preview',
                                                'anchor', 'searchreplace', 'visualblocks', 'code', 'fullscreen',
                                                'insertdatetime', 'media', 'table', 'code', 'help', 'wordcount'
                                            ],
                                            toolbar: 'preview undo redo | blocks | ' +
                                                'bold italic underline forecolor fontsize | alignleft aligncenter ' +
                                                'alignright alignjustify | bullist numlist outdent indent | ' +
                                                'removeformat | help',
                                            content_style: 'body { font-family:Helvetica,Arial,sans-serif; font-size:14px }',
                                            font_size_formats: "8pt 10pt 12pt 14pt 18pt 24pt 36pt",
                                        }}
                                    />
                                    <DialogContentText>
                                        <Typography variant="subtitle1" sx={{ color: blue[800] }}>Issue Instructions</Typography>
                                    </DialogContentText>
                                    <Editor
                                        apiKey={editorAPIKey}
                                        onInit={(evt: any, editor: any) => editorInstructionsRef.current = editor}
                                        initialValue={"Type here for issue instructions..."}
                                        init={{
                                            height: 300,
                                            menubar: true,
                                            branding: false,
                                            plugins: [
                                                'advlist', 'autolink', 'lists', 'link', 'image', 'charmap', 'preview',
                                                'anchor', 'searchreplace', 'visualblocks', 'code', 'fullscreen',
                                                'insertdatetime', 'media', 'table', 'code', 'help', 'wordcount'
                                            ],
                                            toolbar: 'preview undo redo | blocks | ' +
                                                'bold italic underline forecolor fontsize | alignleft aligncenter ' +
                                                'alignright alignjustify | bullist numlist outdent indent | ' +
                                                'removeformat | help',
                                            content_style: 'body { font-family:Helvetica,Arial,sans-serif; font-size:14px }',
                                            font_size_formats: "8pt 10pt 12pt 14pt 18pt 24pt 36pt",
                                        }}
                                    />
                                </Grid>
                            </Paper>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeIssueTemplateEditor} variant="contained" color="warning">Cancel</Button>
                    <Button type="submit" variant="contained" color="success">Save Issue</Button>
                </DialogActions>
            </Dialog>
            {
                alertInfo &&
                <AlertInfoDialog isAlertInfoDialogOpen={isAlertInfoDialogOpen} alertInfo={alertInfo} closeAlertInfoDialog={closeAlertInfoDialog}></AlertInfoDialog>
            }
        </>
    )

}

export default ArcherIssueTemplateEditor